import { useMutation } from "@apollo/client";
import { getValidatorChatMessage } from "enefel/validator";
import { Field, Form, Formik } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { POST_MESSAGE } from "../../../query/ChatQueries";
import palette from "../../../styles/palette";
import QueryError from "../../Error/QueryError";
import Button from "../../Interface/Button";
import Loading from "../../Loading";

const validator = getValidatorChatMessage();

const ChatInputStyle = styled.input`
  background: ${palette.primary.main};
  color: ${palette.primary.contrastText};
  border: none;
  margin-right: 1em;
  padding-left: 0.5em;
  padding-right: 0.5em;
`;

const Submit = styled.div`
  display: flex;

  & > *:first-child {
    flex: 1;
  }
  & > *:last-child {
    flex: none;
  }
`;

type ChatInputProps = {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const ChatInput: React.FC<ChatInputProps> = ({ value, onChange }) => {
  return <ChatInputStyle name="message" value={value} onChange={onChange} />;
};

const ButtonWrapper = styled.div`
  position: relative;
`;

const LoadingWrapper = styled.div`
  position: absolute;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  text-align: center;
  z-index: 1;
`;

type CreateChatMessageFormContentProps = {
  errors: any;
  touched: any;
  loading: boolean;
};

const CreateChatMessageFormContent: React.FC<
  CreateChatMessageFormContentProps
> = ({ errors, touched, loading }) => {
  const { t } = useTranslation();
  return (
    <Form>
      <Submit>
        <Field name="message" type="text">
          {({ field, form, meta }: any) => {
            return <ChatInput value={field.value} onChange={field.onChange} />;
          }}
        </Field>
        <ButtonWrapper>
          {loading && (
            <LoadingWrapper>
              <Loading />
            </LoadingWrapper>
          )}

          <Button type={"submit"} visibility={loading ? "hidden" : "visible"}>
            {t("t:Send", "__Send")}
          </Button>
        </ButtonWrapper>
      </Submit>
    </Form>
  );
};

const PostWrapper = styled.div`
  flex: none;
  padding-top: 1em;
`;

type PostMessageProps = {
  playerId: string;
  chatId: string;
};

const PostMessage: React.FC<PostMessageProps> = React.memo(
  ({ playerId, chatId }) => {
    const [postMessage, { loading, error }] = useMutation(POST_MESSAGE);

    return (
      <PostWrapper>
        {error && <QueryError error={error} />}

        <Formik
          initialValues={{
            message: "",
          }}
          validationSchema={validator}
          onSubmit={({ message }, { resetForm }) => {
            if (message === "") {
              return;
            }
            postMessage({
              variables: { playerId, chatId, message },
            }).then(({ data }) => {
              resetForm();
            });
          }}
        >
          {
            <CreateChatMessageFormContent
              errors={{}}
              touched={{}}
              loading={loading}
            />
          }
        </Formik>
      </PostWrapper>
    );
  }
);

export default PostMessage;
